import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  cardStyle,
  chipStyle,
  dangerButtonStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link, useSearchParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { customerSegmentsRepoFor } from '../lib/repositories/customer-segments-repo.js';
import { customersRepoFor } from '../lib/repositories/customers-repo.js';
import { customerTagsRepoFor } from '../lib/repositories/customer-tags-repo.js';
import { loyaltyTiersRepoFor } from '../lib/repositories/loyalty-repo.js';
import { logger } from '../lib/logger.js';
import {
  matchesSegment,
  money,
  type CustomerSegment,
  type CustomerSegmentCriteria,
  type CustomerTag,
  type SegmentCustomer,
  type TenantCustomerIndex,
  type Tier,
} from '@kilat/shared';

/**
 * /marketing/segments — operator CRUD for reusable saved audience filters.
 *
 * A segment is a named set of AND-combined conditions (tier / tags / spend /
 * visits / recency) over the tenant customer index. The marketing broadcast
 * composer targets a campaign at a saved segment; `runCampaignNow` resolves it
 * server-side. Evaluation is the shared pure `matchesSegment` helper — the same
 * code the dispatcher runs — so the live audience estimate on this page is a
 * faithful preview, not a second implementation that can drift.
 *
 * Writes go through the rules-guarded `customerSegmentsRepoFor` (owner/manager,
 * same gate as customerTags). Every state is explicit — loading / empty / error
 * / saving / estimate-loading — with a next action on each (no dead-ends).
 *
 * COST GUARD: the live estimate reads only the `SAMPLE_CAP` most-recently-active
 * customers (bounded listener, `lastVisitAt desc`), so opening the page costs
 * O(cap) reads. The estimate is labelled as being over that recent sample — it
 * never implies an exact whole-book count it didn't compute.
 */

/** Most-recent customers loaded for the live audience estimate. Bounds read cost. */
const SAMPLE_CAP = 500;

/**
 * Read a `?inactiveDays=N` deep-link param into a validated win-back prefill.
 * The retention-pulse card (AI-tools Slice 2) links here with `inactiveDays=30`
 * so "Create win-back campaign" opens the builder with the lapse filter ready.
 * Anything invalid (missing / non-int / out of range) → no prefill.
 */
function parsePrefillDays(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n <= 0 || n > 3650) return null;
  return n;
}

export function MarketingSegmentsPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const [searchParams] = useSearchParams();
  const prefillInactiveDays = parsePrefillDays(searchParams.get('inactiveDays'));
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const segmentsSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = customerSegmentsRepoFor(tenantId);
    return (cb: (vs: CustomerSegment[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const {
    data: segments,
    loading,
    error,
  } = useTrackedSnapshot<CustomerSegment[]>(['customer-segments', tenantId], segmentsSub);

  const tiersSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = loyaltyTiersRepoFor(tenantId);
    return (cb: (v: Tier[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const { data: tiers } = useTrackedSnapshot<Tier[]>(['segments-tiers', tenantId], tiersSub);

  const tagsSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = customerTagsRepoFor(tenantId);
    return (cb: (v: CustomerTag[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const { data: tags } = useTrackedSnapshot<CustomerTag[]>(['segments-tags', tenantId], tagsSub);

  const sampleSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = customersRepoFor(tenantId);
    return (cb: (v: TenantCustomerIndex[]) => void) =>
      repo.listSnapshotBounded(
        { orderBy: [['lastVisitAt', 'desc']], limitTo: SAMPLE_CAP },
        cb,
        () => cb([]),
      );
  }, [tenantId]);
  const { data: sample } = useTrackedSnapshot<TenantCustomerIndex[]>(
    ['segments-sample', tenantId],
    sampleSub,
  );

  const [editingId, setEditingId] = useState<string | null>(null);

  if (!tenantId) {
    return (
      <Screen
        title={t.t('dashboard.segments.signInTitle')}
        body={t.t('dashboard.segments.signInBody')}
        cta={{ label: t.t('dashboard.segments.backToQueue'), to: '/queue' }}
      />
    );
  }

  const list = (segments ?? []).slice().sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt));
  const editing = editingId ? (list.find((s) => s.id === editingId) ?? null) : null;
  const sampleRows = sample ?? [];
  const tierList = (tiers ?? []).slice().sort((a, b) => a.rank - b.rank);
  const tagList = (tags ?? []).filter((tag) => !tag.archived);

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.segments.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 'var(--space-1) 0 0 0',
            }}
          >
            {loading
              ? t.t('dashboard.segments.loading')
              : t.t(
                  list.length === 1
                    ? 'dashboard.segments.countOne'
                    : 'dashboard.segments.countOther',
                  {
                    count: list.length,
                  },
                )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/marketing" style={ghostLinkStyle}>
            {t.t('dashboard.segments.backToMarketing')}
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.segments.backToQueue')}
          </Link>
        </div>
      </header>

      <p
        style={{
          margin: '0 0 var(--space-5) 0',
          maxWidth: 760,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t.t('dashboard.segments.intro')}
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-6)',
          alignItems: 'flex-start',
        }}
      >
        <section style={{ ...cardStyle, flex: '2 1 320px', minWidth: 0 }}>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.segments.listHeading')}
          </h2>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {loading ? (
              <InlineNote>{t.t('dashboard.segments.loading')}</InlineNote>
            ) : error ? (
              <InlineNote tone="error">
                {t.t('dashboard.segments.loadError', { error: String(error.message ?? error) })}
              </InlineNote>
            ) : list.length === 0 ? (
              <InlineNote>{t.t('dashboard.segments.empty')}</InlineNote>
            ) : (
              list.map((segment) => (
                <SegmentRow
                  key={segment.id}
                  tenantId={tenantId}
                  segment={segment}
                  tiers={tierList}
                  tags={tagList}
                  sample={sampleRows}
                  isEditing={segment.id === editingId}
                  onEdit={() => setEditingId(segment.id)}
                />
              ))
            )}
          </div>
        </section>

        <section style={{ ...cardStyle, flex: '1 1 340px', minWidth: 0, maxWidth: 480 }}>
          <SegmentForm
            tenantId={tenantId}
            editing={editing}
            tiers={tierList}
            tags={tagList}
            sample={sampleRows}
            prefillInactiveDays={prefillInactiveDays}
            onDone={() => setEditingId(null)}
            onCancelEdit={() => setEditingId(null)}
          />
        </section>
      </div>
    </main>
  );
}

// ── row ────────────────────────────────────────────────────────────────────

function SegmentRow({
  tenantId,
  segment,
  tiers,
  tags,
  sample,
  isEditing,
  onEdit,
}: {
  readonly tenantId: string;
  readonly segment: CustomerSegment;
  readonly tiers: readonly Tier[];
  readonly tags: readonly CustomerTag[];
  readonly sample: readonly TenantCustomerIndex[];
  readonly isEditing: boolean;
  readonly onEdit: () => void;
}) {
  const t = useI18n();
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const estimate = useMemo(
    () => countMatches(sample, segment.criteria),
    [sample, segment.criteria],
  );

  async function toggleActive() {
    setBusy(true);
    try {
      await customerSegmentsRepoFor(tenantId).update(segment.id, {
        active: !segment.active,
        updatedAt: Timestamp.now(),
      });
    } catch (e) {
      logger.error('segment toggle failed', { error: String(e), id: segment.id });
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await customerSegmentsRepoFor(tenantId).delete(segment.id);
      logger.info('segment deleted', { tenantId, id: segment.id });
    } catch (e) {
      logger.error('segment delete failed', { error: String(e), id: segment.id });
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: isEditing ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
        background: isEditing ? 'rgba(13,122,79,0.04)' : 'var(--color-surface)',
        opacity: segment.active ? 1 : 0.82,
      }}
    >
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 500, overflowWrap: 'anywhere' }}>{segment.name}</p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {describeCriteria(segment.criteria, tiers, tags, t)}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.segments.estimate', { count: estimate, sample: sample.length })}
        </p>
      </div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        <span
          aria-label={t.t('dashboard.segments.statusLabel')}
          style={{
            background: segment.active ? 'rgba(13,122,79,0.10)' : 'var(--color-border)',
            color: segment.active ? 'var(--color-primary)' : 'var(--color-on-surface-muted)',
            padding: '4px 10px',
            borderRadius: 'var(--radius-pill)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {segment.active ? t.t('dashboard.segments.active') : t.t('dashboard.segments.inactive')}
        </span>
        {confirmingDelete ? (
          <>
            <button
              type="button"
              onClick={() => void doDelete()}
              disabled={busy}
              style={dangerButtonStyle}
            >
              {busy ? t.t('dashboard.segments.deleting') : t.t('dashboard.segments.confirmDelete')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              style={miniGhostButtonStyle}
            >
              {t.t('dashboard.segments.cancel')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => void toggleActive()}
              disabled={busy}
              style={miniGhostButtonStyle}
            >
              {segment.active ? t.t('dashboard.segments.pause') : t.t('dashboard.segments.resume')}
            </button>
            <button
              type="button"
              onClick={onEdit}
              disabled={isEditing}
              style={miniGhostButtonStyle}
            >
              {isEditing ? t.t('dashboard.segments.editing') : t.t('dashboard.segments.edit')}
            </button>
            <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
              {t.t('dashboard.segments.delete')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ── form (create + edit) ─────────────────────────────────────────────────────

interface FormState {
  name: string;
  tierIds: string[];
  tagIds: string[];
  minSpend: string; // RM major units
  maxSpend: string;
  minVisits: string;
  maxVisits: string;
  inactiveDays: string;
  newWithinDays: string;
  active: boolean;
}

function SegmentForm({
  tenantId,
  editing,
  tiers,
  tags,
  sample,
  prefillInactiveDays,
  onDone,
  onCancelEdit,
}: {
  readonly tenantId: string;
  readonly editing: CustomerSegment | null;
  readonly tiers: readonly Tier[];
  readonly tags: readonly CustomerTag[];
  readonly sample: readonly TenantCustomerIndex[];
  /** Win-back deep-link prefill (`?inactiveDays=N`); only applied in create mode. */
  readonly prefillInactiveDays: number | null;
  readonly onDone: () => void;
  readonly onCancelEdit: () => void;
}) {
  const t = useI18n();
  // Only prefill a brand-new segment — never override the fields of one being edited.
  const prefillDays = editing === null ? prefillInactiveDays : null;
  const initial = useMemo(
    () =>
      formFromSegment(
        editing,
        prefillDays !== null
          ? {
              inactiveDays: prefillDays,
              name: t.t('dashboard.segments.prefill.defaultName', { days: prefillDays }),
            }
          : undefined,
      ),
    [editing, prefillDays, t],
  );
  const [state, setState] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  useEffect(() => {
    setState(initial);
    setError(null);
    setSavedNotice(null);
  }, [editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function patch(p: Partial<FormState>) {
    setState((s) => ({ ...s, ...p }));
  }

  function toggleTier(id: string) {
    setState((s) => ({
      ...s,
      tierIds: s.tierIds.includes(id) ? s.tierIds.filter((x) => x !== id) : [...s.tierIds, id],
    }));
  }
  function toggleTag(id: string) {
    setState((s) => ({
      ...s,
      tagIds: s.tagIds.includes(id) ? s.tagIds.filter((x) => x !== id) : [...s.tagIds, id],
    }));
  }

  const draftCriteria = useMemo(() => criteriaFromForm(state), [state]);
  const estimate = useMemo(() => countMatches(sample, draftCriteria), [sample, draftCriteria]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSavedNotice(null);

    const trimmedName = state.name.trim();
    if (!trimmedName) {
      setError(t.t('dashboard.segments.form.errName'));
      return;
    }
    const minSpend = parseSen(state.minSpend);
    const maxSpend = parseSen(state.maxSpend);
    if (minSpend !== undefined && maxSpend !== undefined && minSpend > maxSpend) {
      setError(t.t('dashboard.segments.form.errSpendRange'));
      return;
    }
    const minVisits = parseCount(state.minVisits);
    const maxVisits = parseCount(state.maxVisits);
    if (minVisits !== undefined && maxVisits !== undefined && minVisits > maxVisits) {
      setError(t.t('dashboard.segments.form.errVisitsRange'));
      return;
    }

    const criteria = criteriaFromForm(state);

    setSubmitting(true);
    try {
      const now = Timestamp.now();
      if (editing) {
        await customerSegmentsRepoFor(tenantId).set(editing.id, {
          id: editing.id,
          name: trimmedName,
          criteria,
          active: state.active,
          createdAt: editing.createdAt,
          updatedAt: now,
        });
        setSavedNotice(t.t('dashboard.segments.form.updated', { name: trimmedName }));
        onDone();
      } else {
        const id = `seg-${Math.random().toString(36).slice(2, 8)}`;
        await customerSegmentsRepoFor(tenantId).set(id, {
          id,
          name: trimmedName,
          criteria,
          active: state.active,
          createdAt: now,
          updatedAt: now,
        });
        setSavedNotice(t.t('dashboard.segments.form.created', { name: trimmedName }));
        setState(formFromSegment(null));
      }
    } catch (e2) {
      logger.error('segment save failed', { error: String(e2), editing: editing?.id });
      setError((e2 as Error).message ?? t.t('dashboard.segments.form.errSave'));
    } finally {
      setSubmitting(false);
    }
  }

  const isEditMode = editing !== null;

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <header>
        <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
          {isEditMode
            ? t.t('dashboard.segments.form.editTitle', { name: editing.name })
            : t.t('dashboard.segments.form.createTitle')}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 'var(--space-1) 0 0 0',
          }}
        >
          {isEditMode
            ? t.t('dashboard.segments.form.editSubtitle')
            : t.t('dashboard.segments.form.createSubtitle')}
        </p>
      </header>

      {prefillDays !== null ? (
        <p
          role="status"
          style={{
            margin: 0,
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-sm)',
            background: 'rgba(13,122,79,0.06)',
            border: '1px solid rgba(13,122,79,0.18)',
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {t.t('dashboard.segments.prefill.notice', { days: prefillDays })}
        </p>
      ) : null}

      {savedNotice ? (
        <p
          role="status"
          style={{
            margin: 0,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {savedNotice}
        </p>
      ) : null}

      <label style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.segments.form.nameLabel')}</span>
        <input
          required
          value={state.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder={t.t('dashboard.segments.form.namePlaceholder')}
          maxLength={80}
          style={inputStyle}
        />
      </label>

      <fieldset
        style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}
      >
        <legend style={fieldLabel}>{t.t('dashboard.segments.form.criteriaLegend')}</legend>
        <p style={{ ...helpTextStyle, margin: 0 }}>{t.t('dashboard.segments.form.criteriaHint')}</p>
      </fieldset>

      {/* Tier */}
      <div style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.segments.form.tiersLabel')}</span>
        {tiers.length === 0 ? (
          <p style={{ ...helpTextStyle, margin: 0 }}>{t.t('dashboard.segments.form.tiersEmpty')}</p>
        ) : (
          <div style={chipRow}>
            {tiers.map((tier) => {
              const selected = state.tierIds.includes(tier.id);
              return (
                <button
                  key={tier.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleTier(tier.id)}
                  style={chipStyle(selected)}
                >
                  {tier.name[t.locale] ?? tier.name['en'] ?? tier.id}
                </button>
              );
            })}
          </div>
        )}
        <span style={helpTextStyle}>{t.t('dashboard.segments.form.tiersHint')}</span>
      </div>

      {/* Tags */}
      <div style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.segments.form.tagsLabel')}</span>
        {tags.length === 0 ? (
          <p style={{ ...helpTextStyle, margin: 0 }}>
            {t.t('dashboard.segments.form.tagsEmpty')}{' '}
            <Link to="/loyalty/tags" style={{ color: 'var(--color-primary)' }}>
              {t.t('dashboard.segments.form.tagsEmptyLink')}
            </Link>
          </p>
        ) : (
          <div style={chipRow}>
            {tags.map((tag) => {
              const selected = state.tagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleTag(tag.id)}
                  style={chipStyle(selected)}
                >
                  {tag.label[t.locale] ?? tag.label['en'] ?? tag.id}
                </button>
              );
            })}
          </div>
        )}
        <span style={helpTextStyle}>{t.t('dashboard.segments.form.tagsHint')}</span>
      </div>

      {/* Spend band */}
      <div style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.segments.form.spendLegend')}</span>
        <div style={twoCol}>
          <label style={fieldGrid}>
            <span style={helpTextStyle}>{t.t('dashboard.segments.form.minLabel')}</span>
            <input
              type="number"
              min={0}
              step="any"
              value={state.minSpend}
              onChange={(e) => patch({ minSpend: e.target.value })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
          <label style={fieldGrid}>
            <span style={helpTextStyle}>{t.t('dashboard.segments.form.maxLabel')}</span>
            <input
              type="number"
              min={0}
              step="any"
              value={state.maxSpend}
              onChange={(e) => patch({ maxSpend: e.target.value })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
        </div>
      </div>

      {/* Visits band */}
      <div style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.segments.form.visitsLegend')}</span>
        <div style={twoCol}>
          <label style={fieldGrid}>
            <span style={helpTextStyle}>{t.t('dashboard.segments.form.minLabel')}</span>
            <input
              type="number"
              min={0}
              step={1}
              value={state.minVisits}
              onChange={(e) => patch({ minVisits: e.target.value })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
          <label style={fieldGrid}>
            <span style={helpTextStyle}>{t.t('dashboard.segments.form.maxLabel')}</span>
            <input
              type="number"
              min={0}
              step={1}
              value={state.maxVisits}
              onChange={(e) => patch({ maxVisits: e.target.value })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
        </div>
      </div>

      {/* Recency */}
      <div style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.segments.form.recencyLegend')}</span>
        <label style={fieldGrid}>
          <span style={helpTextStyle}>{t.t('dashboard.segments.form.inactiveDaysLabel')}</span>
          <input
            type="number"
            min={1}
            step={1}
            value={state.inactiveDays}
            onChange={(e) => patch({ inactiveDays: e.target.value })}
            placeholder="30"
            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          />
          <span style={helpTextStyle}>{t.t('dashboard.segments.form.inactiveDaysHint')}</span>
        </label>
        <label style={fieldGrid}>
          <span style={helpTextStyle}>{t.t('dashboard.segments.form.newWithinDaysLabel')}</span>
          <input
            type="number"
            min={1}
            step={1}
            value={state.newWithinDays}
            onChange={(e) => patch({ newWithinDays: e.target.value })}
            placeholder="14"
            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          />
          <span style={helpTextStyle}>{t.t('dashboard.segments.form.newWithinDaysHint')}</span>
        </label>
      </div>

      {/* Live estimate */}
      <div
        role="status"
        style={{
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(13,122,79,0.06)',
          border: '1px solid rgba(13,122,79,0.18)',
          fontSize: 'var(--text-meta)',
        }}
      >
        <p style={{ margin: 0, fontWeight: 500, color: 'var(--color-primary)' }}>
          {t.t('dashboard.segments.form.estimate', { count: estimate, sample: sample.length })}
        </p>
        <p style={{ ...helpTextStyle, marginTop: 'var(--space-1)' }}>
          {t.t('dashboard.segments.form.estimateNote', { sample: sample.length })}
        </p>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={state.active}
          onChange={(e) => patch({ active: e.target.checked })}
          style={{ width: 18, height: 18, cursor: 'pointer' }}
        />
        <span style={fieldLabel}>
          {state.active
            ? t.t('dashboard.segments.form.activeOn')
            : t.t('dashboard.segments.form.activeOff')}
        </span>
      </label>

      {error ? (
        <p
          role="alert"
          style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...primaryButtonStyle,
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting
            ? t.t('dashboard.segments.form.saving')
            : isEditMode
              ? t.t('dashboard.segments.form.saveChanges')
              : t.t('dashboard.segments.form.create')}
        </button>
        {isEditMode ? (
          <button
            type="button"
            onClick={onCancelEdit}
            disabled={submitting}
            style={ghostButtonStyle}
          >
            {t.t('dashboard.segments.form.cancelEdit')}
          </button>
        ) : null}
      </div>
    </form>
  );
}

// ── criteria helpers ─────────────────────────────────────────────────────────

function parseCount(s: string): number | undefined {
  const trimmed = s.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n);
}

function parseSen(rm: string): number | undefined {
  const trimmed = rm.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

function parsePositive(s: string): number | undefined {
  const n = parseCount(s);
  return n !== undefined && n > 0 ? n : undefined;
}

function criteriaFromForm(state: FormState): CustomerSegmentCriteria {
  const minSpend = parseSen(state.minSpend);
  const maxSpend = parseSen(state.maxSpend);
  const minVisits = parseCount(state.minVisits);
  const maxVisits = parseCount(state.maxVisits);
  const inactiveDays = parsePositive(state.inactiveDays);
  const newWithinDays = parsePositive(state.newWithinDays);
  return {
    ...(state.tierIds.length ? { tierIdIn: state.tierIds } : {}),
    ...(state.tagIds.length ? { tagIdsAny: state.tagIds } : {}),
    ...(minSpend !== undefined ? { minSpendSen: minSpend } : {}),
    ...(maxSpend !== undefined ? { maxSpendSen: maxSpend } : {}),
    ...(minVisits !== undefined ? { minVisits } : {}),
    ...(maxVisits !== undefined ? { maxVisits } : {}),
    ...(inactiveDays !== undefined ? { inactiveDays } : {}),
    ...(newWithinDays !== undefined ? { newWithinDays } : {}),
  };
}

function formFromSegment(
  s: CustomerSegment | null,
  prefill?: { readonly inactiveDays?: number; readonly name?: string },
): FormState {
  if (!s) {
    return {
      name: prefill?.name ?? '',
      tierIds: [],
      tagIds: [],
      minSpend: '',
      maxSpend: '',
      minVisits: '',
      maxVisits: '',
      inactiveDays: prefill?.inactiveDays !== undefined ? String(prefill.inactiveDays) : '',
      newWithinDays: '',
      active: true,
    };
  }
  const c = s.criteria;
  return {
    name: s.name,
    tierIds: c.tierIdIn ? [...c.tierIdIn] : [],
    tagIds: c.tagIdsAny ? [...c.tagIdsAny] : [],
    minSpend: c.minSpendSen !== undefined ? String(c.minSpendSen / 100) : '',
    maxSpend: c.maxSpendSen !== undefined ? String(c.maxSpendSen / 100) : '',
    minVisits: c.minVisits !== undefined ? String(c.minVisits) : '',
    maxVisits: c.maxVisits !== undefined ? String(c.maxVisits) : '',
    inactiveDays: c.inactiveDays !== undefined ? String(c.inactiveDays) : '',
    newWithinDays: c.newWithinDays !== undefined ? String(c.newWithinDays) : '',
    active: s.active,
  };
}

function toSegmentCustomer(c: TenantCustomerIndex): SegmentCustomer {
  return {
    tierId: c.tierId,
    tags: c.tags,
    lifetimeSpendSen: c.lifetimeSpendSen,
    visits: c.visits,
    firstVisitAt: tsToMillis(c.firstVisitAt),
    lastVisitAt: tsToMillis(c.lastVisitAt),
  };
}

function countMatches(
  sample: readonly TenantCustomerIndex[],
  criteria: CustomerSegmentCriteria,
): number {
  const nowMs = Date.now();
  let n = 0;
  for (const c of sample) {
    if (matchesSegment(toSegmentCustomer(c), criteria, nowMs)) n += 1;
  }
  return n;
}

function describeCriteria(
  criteria: CustomerSegmentCriteria,
  tiers: readonly Tier[],
  tags: readonly CustomerTag[],
  t: I18n,
): string {
  const parts: string[] = [];
  if (criteria.tierIdIn && criteria.tierIdIn.length > 0) {
    const names = criteria.tierIdIn.map(
      (id) =>
        tiers.find((x) => x.id === id)?.name[t.locale] ??
        tiers.find((x) => x.id === id)?.name['en'] ??
        id,
    );
    parts.push(t.t('dashboard.segments.crit.tierIn', { tiers: names.join(', ') }));
  }
  if (criteria.tagIdsAny && criteria.tagIdsAny.length > 0) {
    const names = criteria.tagIdsAny.map(
      (id) =>
        tags.find((x) => x.id === id)?.label[t.locale] ??
        tags.find((x) => x.id === id)?.label['en'] ??
        id,
    );
    parts.push(t.t('dashboard.segments.crit.tagsAny', { tags: names.join(', ') }));
  }
  if (criteria.minSpendSen !== undefined) {
    parts.push(
      t.t('dashboard.segments.crit.minSpend', { amount: money.format(criteria.minSpendSen) }),
    );
  }
  if (criteria.maxSpendSen !== undefined) {
    parts.push(
      t.t('dashboard.segments.crit.maxSpend', { amount: money.format(criteria.maxSpendSen) }),
    );
  }
  if (criteria.minVisits !== undefined) {
    parts.push(t.t('dashboard.segments.crit.minVisits', { n: criteria.minVisits }));
  }
  if (criteria.maxVisits !== undefined) {
    parts.push(t.t('dashboard.segments.crit.maxVisits', { n: criteria.maxVisits }));
  }
  if (criteria.inactiveDays !== undefined) {
    parts.push(t.t('dashboard.segments.crit.inactiveDays', { n: criteria.inactiveDays }));
  }
  if (criteria.newWithinDays !== undefined) {
    parts.push(t.t('dashboard.segments.crit.newWithinDays', { n: criteria.newWithinDays }));
  }
  return parts.length > 0 ? parts.join(' · ') : t.t('dashboard.segments.crit.all');
}

function tsMs(ts: CustomerSegment['createdAt']): number {
  return ts.toMillis?.() ?? ts.seconds * 1000;
}

function tsToMillis(ts: { toMillis?: () => number; seconds: number }): number {
  return ts.toMillis?.() ?? ts.seconds * 1000;
}

// ── shared UI bits ───────────────────────────────────────────────────────────

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: 'var(--space-3) var(--space-4)',
        fontSize: 'var(--text-meta)',
        color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </p>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 380 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── styles (mirror /loyalty/challenges) ──────────────────────────────────────

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldGrid: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)', minWidth: 0 };

const twoCol: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 'var(--space-3)',
};

const chipRow: React.CSSProperties = { display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' };

const helpTextStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};
